import React from 'react';
import { motion } from 'framer-motion';
import { VegDotIcon, DietaryOverlayIcon } from '../components/DietaryIcon';
import { parsePrice } from "../utils/priceUtils";

interface PrimePopularItemCardProps {
  item: any;
  index: number;
  primaryColor: string;
  onItemClick: (item: any) => void;
  onAddToCart: (item: any) => void;
  showAddToCart?: boolean;
}

/* -------------------- Color Utils -------------------- */
const hexToRgb = (hex: string) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null;
};

// Utility to create light version of primary color for backgrounds
const lightenColor = (hex: string, amount: number = 0.95) => {
  const rgb = hexToRgb(hex);
  if (!rgb) return '#f9fafb'; // fallback to gray-50
  
  return `rgb(${Math.min(255, rgb.r + (255 - rgb.r) * amount)}, ${Math.min(255, rgb.g + (255 - rgb.g) * amount)}, ${Math.min(255, rgb.b + (255 - rgb.b) * amount)})`;
};

const PrimePopularItemCard: React.FC<PrimePopularItemCardProps> = React.memo(({
  item,
  index,
  primaryColor,
  onItemClick,
  onAddToCart,
  showAddToCart = true,
}) => {

  return (
    <motion.div
      className="bg-gradient-to-br from-white via-gray-50 to-white rounded-3xl shadow-2xl border border-gray-100/50 overflow-hidden cursor-pointer h-full group relative"
      onClick={() => onItemClick(item)}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1, duration: 0.6, ease: "easeOut" }}
      whileHover={{ 
        scale: 1.02,
        transition: { duration: 0.2 }
      }}
    >
      {/* Premium glow effect */}
      <div 
        className="absolute inset-0 rounded-3xl opacity-0 group-hover:opacity-40 transition-opacity duration-700 z-10"
        style={{
          background: `radial-gradient(circle at 50% 50%, ${primaryColor}20, transparent 70%)`
        }}
      />
      
      {/* Image section */}
      <div className="aspect-[5/4] w-full relative overflow-hidden">
        {item.image ? (
          <>
            <img
              src={item.image}
              alt={item.name || 'Menu item'}
              className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-700"
              loading="lazy"
            />
            {/* Premium overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent" />
          </>
        ) : (
          <div 
            className="w-full h-full relative flex items-center justify-center"
            style={{
              background: `linear-gradient(135deg, ${primaryColor}10, ${primaryColor}20, ${primaryColor}10)`
            }}
          >
            <span 
              className="font-bold text-4xl sm:text-6xl"
              style={{ color: primaryColor }}
            >
              {item.name?.charAt(0)?.toUpperCase() || '?'}
            </span>
          </div>
        )}
        
        {/* Dietary Icons Overlay */}
        <div className="absolute top-2 right-2 z-10">
          <DietaryOverlayIcon item={item} size="md" />
        </div>
        
        {/* Luxury corner decoration */}
        <div className="absolute top-0 right-0 w-16 h-16 sm:w-24 sm:h-24">
          <div 
            className="w-full h-full opacity-10"
            style={{
              background: `conic-gradient(from 0deg at 100% 0%, ${primaryColor}, transparent 90deg)`
            }}
          />
        </div>
      </div>
      
      {/* Content section */}
      <div 
        className="p-4 sm:p-6"
        style={{ backgroundColor: lightenColor(primaryColor, 0.95) }}
      >
        {/* Title */}
        <div className="flex items-start gap-2 mb-2 sm:mb-3">
          <div className="flex-shrink-0 mt-1">
            <VegDotIcon item={item} size="md" />
          </div>
          <h3 className="font-bold text-gray-900 text-lg sm:text-xl line-clamp-1 flex-1">
            {item.name}
          </h3>
        </div>
        
        {/* Description */}
        {item.description && (
          <p className="text-gray-600 text-sm mb-3 sm:mb-4 line-clamp-2">
            {item.description}
          </p>
        )}
        
        {/* Price */}
        <div className="mb-3 sm:mb-4">
          <span 
            className="font-bold text-xl sm:text-2xl"
            style={{ color: primaryColor }}
          >
            ${parsePrice(item.price).toFixed(2)}
          </span>
        </div>
        
        {/* Add to Cart Button - Only show when TPN is active */}
        {showAddToCart && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onAddToCart(item);
            }}
            aria-label={`Add ${item.name} to cart`}
            className="w-full text-white px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg font-semibold 
                       transition-all duration-200 hover:scale-105 
                       shadow-md hover:shadow-lg hover:brightness-90 
                       focus:outline-none focus:ring-2 focus:ring-offset-2 
                       min-h-[44px] text-sm sm:text-base"
            style={{
              backgroundColor: primaryColor
            }}
          >
            Add to Cart
          </button>
        )}
      </div>
    </motion.div>
  );
});

PrimePopularItemCard.displayName = 'PrimePopularItemCard';

export default PrimePopularItemCard;
